import { useState, useContext, useEffect } from "react"
import { gql, useLazyQuery } from "@apollo/client"
import _ from "lodash"
import { Field, useFormikContext } from "formik"
import { MultiSelect } from "primereact/multiselect"
import { FormContext } from "../../../context/formContext"
import { UserContext } from "../../../context/userContext"
import { LoaderSmall } from "../../Loaders"
import { LogError } from "../../../helpers/logger"

import styles from "../../../styles/Form.module.css"

export default function ManageLocationIds({ field, block, index }) {
  const formik = useFormikContext()
  const form = useContext(FormContext)
  const userCtx = useContext(UserContext)
  const [activeLocations, setActiveLocations] = useState([])
  const [inactiveLocations, setInactiveLocations] = useState([])
  const [visible, setVisible] = useState("none")

  const checkIfRequired = () => {
    try {
      return JSON.parse(field.field_validation.required[0])
    } catch {
      return false
    }
  }
  const isRequired = checkIfRequired()

  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  /* INIT LOCATIONS */
  const GET_LOCATIONS = gql`
    query getLocations(
      $staff_id: ID!
      $organization_id: ID!
      $participant_membership_id: ID!
    ) {
      locations(staff_id: $staff_id, organization_id: $organization_id) {
        key: location_id
        label: location_name
        status: location_status
      }
      locationsByMembershipId(
        staff_id: $staff_id
        organization_id: $organization_id
        participant_membership_id: $participant_membership_id
      ) {
        key: location_id
        label: location_name
      }
    }
  `

  const [getLocations, { loading }] = useLazyQuery(GET_LOCATIONS, {
    fetchPolicy: "network-only",
  })

  async function initLocations() {
    // Register field as loading in FormContext
    form && form.registerLoadingField(field)

    try {
      const { data } = await getLocations({
        variables: {
          staff_id: userCtx?.session?.user?.staff_id,
          organization_id: userCtx?.session?.user?.organization_id,
          participant_membership_id: userCtx?.participant_membership_id,
        },
      })

      const { locations, locationsByMembershipId } = data || {}

      if (!_.isEmpty(locations)) {
        // split lists
        const activeLocationsList = locations
          .filter((location) => location.status === "ACTIVE")
          .map(({ status, ...rest }) => rest)

        const inactiveLocationsList = locations
          .filter((location) => location.status === "INACTIVE")
          .map(({ status, ...rest }) => rest)

        setActiveLocations(activeLocationsList)
        setInactiveLocations(inactiveLocationsList)

        // Only set initial value if current value is empty
        const currentValue = formik.values[field.field_name]
        if (!currentValue || currentValue.length === 0) {
          // 🟦 Normalize initial values (avoids null)
          const normalizedInitial = (locationsByMembershipId || []).map((item) => {
            const match =
              activeLocationsList.find((x) => x.key === item.key) ||
              inactiveLocationsList.find((x) => x.key === item.key)

            // if the key exists in the new data, we replace label and other fields
            return match ? match : item
          })

          formik.setFieldValue(field.field_name, normalizedInitial)
        }
      } else {
        // save empty array if nothing, avoids null
        formik.setFieldValue(field.field_name, [])
      }
    } catch (error) {
      LogError("Error fetching locations:", error)
      formik.setFieldValue(field.field_name, [])
    }
    finally {
      // Ensure loading state is cleared even if there's an error
      form && form.completeLoadingField(field)
    }
  }

  useEffect(() => {
    initLocations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (block === "participant__manage_services") {
      const manageServicesType = formik.getFieldProps("manage_services_type")
      setVisible("none")
      if (
        manageServicesType.value === "Location assignment" ||
        manageServicesType.value === "Location reassignment"
      ) {
        setVisible("block")
      }
    } else {
      setVisible("block")
    }
  }, [formik])

  // =========================================
  // 🔵 CORRECTION: maintain consistency by key
  // =========================================
  const normalizeByKey = (items = []) => {
    return items.map((item) => {
      const match =
        activeLocations.find((x) => x.key === item.key) ||
        inactiveLocations.find((x) => x.key === item.key)

      return match ? match : item
    })
  }

  const handleSelectLocationIds = (value) => {
    if (value === null) {
      formik.setFieldValue(field.field_name, "")
      return
    }

    const fieldCurrentValue = formik.values[field.field_name] || []

    const currentInactiveLocations =
      fieldCurrentValue.filter((item) =>
        inactiveLocations.some((inactive) => inactive.key === item.key)
      ) || []

    // Combine while maintaining consistency by key
    const newValue = normalizeByKey([
      ...currentInactiveLocations,
      ...value
    ])

    formik.setFieldValue(field.field_name, newValue)
  }

  const handleDeselectInactiveLocationIds = (value) => {
    const fieldCurrentValue = formik.values[field.field_name] || []

    if (value.length === 0) {
      const selectedLocations = fieldCurrentValue.filter((item) =>
        activeLocations.some((active) => active.key === item.key)
      )

      formik.setFieldValue(field.field_name, normalizeByKey(selectedLocations))
      return
    }

    const currentActiveLocations = fieldCurrentValue.filter((item) =>
      activeLocations.some((active) => active.key === item.key)
    )

    const filtered = normalizeByKey([...currentActiveLocations, ...value])

    setInactiveLocations(value)
    formik.setFieldValue(field.field_name, filtered)
  }

  const handleMultiSelectRender = (formikField) => {
    const currentValues = normalizeByKey(formikField?.value || [])

    const inactiveSelected = currentValues.filter((item) =>
      inactiveLocations.some((x) => x.key === item.key)
    )

    const activeSelected = currentValues.filter((item) =>
      activeLocations.some((x) => x.key === item.key)
    )

    const renderInactiveLocations = () => {
      if (inactiveSelected.length === 0) return null

      return (
        <div>
          <label className={styles.label}>Inactive Locations ID(s)</label>
          <div className={styles.description}>
            Inactive Locations can only be deselected.
          </div>

          <MultiSelect
            placeholder="Unselect only"
            id={field.field_name}
            name={formikField.name}
            value={inactiveSelected}
            onChange={(e) => handleDeselectInactiveLocationIds(e.value)}
            onBlur={formik.handleBlur}
            disabled={isDisabled}
            options={inactiveSelected}
          />
        </div>
      )
    }

    return (
      <>
        <MultiSelect
          placeholder="Please select..."
          id={field.field_name}
          name={formikField.name}
          value={activeSelected}
          onChange={(e) => handleSelectLocationIds(e.value)}
          onBlur={formik.handleBlur}
          disabled={isDisabled}
          options={activeLocations}
        />

        {renderInactiveLocations()}
      </>
    )
  }

  if (visible === "none") return null
  if (loading) return <LoaderSmall />

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: visible
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>

      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}

      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, meta }) => (
            <>
              {handleMultiSelectRender(formikField)}
              {meta.touched && meta.error && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}
